import { Star } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal } from "@/components/landing/Reveal";

const G_COLORS = ["#4285F4", "#EA4335", "#FBBC05", "#4285F4", "#34A853", "#EA4335"];

const GoogleBadge = () => (
  <div data-testid="reviews-badge" className="relative w-48 h-48 sm:w-60 sm:h-60 shrink-0">
    <motion.div
      className="absolute inset-2 rounded-full bg-tlime/25 blur-2xl"
      animate={{ opacity: [0.35, 0.85, 0.35], scale: [0.95, 1.05, 0.95] }}
      transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.svg
      viewBox="0 0 200 200"
      className="absolute inset-0 w-full h-full"
      animate={{ rotate: 360 }}
      transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
    >
      <defs>
        <path id="badge-ring" d="M100,100 m-88,0 a88,88 0 1,1 176,0 a88,88 0 1,1 -176,0" />
      </defs>
      <text style={{ fontSize: 13, fontWeight: 800, letterSpacing: 2.6 }} className="fill-tlime uppercase">
        <textPath href="#badge-ring">150+ five-star Google reviews ★ Rated 5.0 in Perth ★&#160;</textPath>
      </text>
    </motion.svg>
    <div className="absolute inset-[30px] sm:inset-[36px] rounded-full bg-white shadow-2xl shadow-black/40 border-[3px] border-tlime flex flex-col items-center justify-center gap-0.5">
      <span className="font-extrabold text-xl sm:text-2xl leading-none tracking-tight" style={{ fontFamily: "arial, sans-serif" }}>
        {"Google".split("").map((c, i) => (
          <span key={i} style={{ color: G_COLORS[i] }}>{c}</span>
        ))}
      </span>
      <span className="flex items-center gap-0.5 my-0.5">
        {[...Array(5)].map((_, i) => (
          <motion.span
            key={i}
            animate={{ scale: [1, 1.45, 1], rotate: [0, 12, 0] }}
            transition={{ duration: 0.55, delay: i * 0.14, repeat: Infinity, repeatDelay: 2.2, ease: "easeInOut" }}
            className="inline-flex"
          >
            <Star className="w-4 h-4 sm:w-5 sm:h-5 fill-[#FBBC05] text-[#FBBC05]" />
          </motion.span>
        ))}
      </span>
      <span className="font-bebas text-navy text-2xl sm:text-3xl leading-none">150+</span>
      <span className="text-[9px] sm:text-[10px] font-extrabold tracking-[0.18em] text-navy/60 uppercase">5-star reviews</span>
    </div>
  </div>
);

const REVIEWS = [
  { text: "Communication was prompt and clear from start to finish. The plumber and electrician were professional, polite and very helpful — smooth and stress free. Highly recommend!", name: "Dali R." },
  { text: "The team installed a leak detection unit and a power outlet for it. Friendly, professional, on schedule, and the work is done to a high standard. Highly recommended.", name: "Jaco Kok" },
  { text: "Timely and gold standard. Efficient, did an outstanding job, polite and considerate. They turned up when they said they would and cleared all mess behind them.", name: "Nicola Ryan" },
  { text: "I've used them quite a few times over a couple of years. Always a very good job in every way and best prices compared with others. Can't thank them enough.", name: "Ela S." },
  { text: "They attended my job within 4 hours of my call, and kept me updated with what was happening the whole time. I couldn't recommend them enough!", name: "Brayden Luksa" },
  { text: "Just so easy to communicate with the team and everything went smoothly. I am literally coming back and asking for new jobs with them right now.", name: "Willy Tang" },
  { text: "Very easy, streamlined process. Our technician was knowledgeable and explained things clearly. Didn't try to add on unnecessary work or fees. Would recommend.", name: "C Sinclair" },
];

const Card = ({ review, testId }) => (
  <div
    data-testid={testId}
    className="w-[20rem] sm:w-[24rem] shrink-0 rounded-3xl bg-white p-6 sm:p-7 shadow-lg shadow-black/20 hover:-translate-y-1 hover:shadow-2xl transition-[transform,box-shadow] duration-300"
  >
    <div className="flex items-center gap-1 mb-3">
      {[...Array(5)].map((_, i) => (
        <Star key={i} className="w-4 h-4 fill-tpink text-tpink" />
      ))}
    </div>
    <p className="text-sm text-navy/75 leading-relaxed">"{review.text}"</p>
    <p className="mt-4 font-bebas text-xl text-navy tracking-wide">
      {review.name} <span className="text-navy/40 text-sm font-manrope font-semibold">· Google review</span>
    </p>
  </div>
);

const Row = ({ items, reverse = false, testId }) => (
  <div data-testid={testId} className="overflow-hidden py-3 [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
    <div className={`flex w-max gap-5 ${reverse ? "animate-marquee-slow-reverse" : "animate-marquee-slow"}`}>
      {[0, 1].map((half) => (
        <div key={half} className="flex shrink-0 gap-5 pr-5">
          {items.map((r, i) => (
            <Card key={`${half}-${i}`} review={r} testId={`${testId}-card-${half}-${i}`} />
          ))}
        </div>
      ))}
    </div>
  </div>
);

export const Reviews = () => (
  <section data-testid="reviews-section" className="py-24 sm:py-32 bg-navy noise-overlay relative overflow-hidden">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
      <Reveal className="flex flex-col sm:flex-row items-start sm:items-end gap-8 justify-between">
        <div>
          <p className="font-bold text-xs tracking-[0.3em] text-tlime uppercase mb-4">Chapter 05 — Real Google reviews</p>
          <h2 className="font-bebas text-5xl sm:text-6xl lg:text-7xl text-white leading-[0.9] max-w-3xl">
            DON'T TAKE <span className="text-tpink">OUR WORD</span> FOR IT.
          </h2>
          <div className="mt-5 flex items-center gap-1.5">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 fill-tlime text-tlime" />
            ))}
            <span className="ml-2 text-sm font-bold text-white/60">150+ five-star Google reviews</span>
          </div>
        </div>
        <GoogleBadge />
      </Reveal>
    </div>
    <Reveal delay={0.15} className="mt-12 space-y-5 relative">
      <Row items={REVIEWS.slice(0, 4)} testId="reviews-row-1" />
      <Row items={[...REVIEWS.slice(4), REVIEWS[0]]} reverse testId="reviews-row-2" />
    </Reveal>
  </section>
);
